<template>
  <q-layout view="lHh Lpr lFf">
    <q-page-container>
      <q-page class="flex flex-center bg-gradient">
        <div class="change-password-container">
          <!-- Título -->
          <div class="text-center q-mb-xl">
            <h4 class="text-h4 text-weight-bold q-mb-sm q-mt-none">
              {{ isReset ? 'Redefinir Senha' : 'Alterar Senha' }}
            </h4>
            <p class="text-grey-7">
              {{ isReset ? 'Crie uma nova senha para sua conta' : 'Atualize sua senha de acesso' }}
            </p>
          </div>

          <!-- Card de Alteração de Senha -->
          <q-card class="change-password-card q-pa-lg shadow-10">
            <q-card-section>
              <q-form @submit="handleSubmit" class="q-gutter-md">
                <!-- Campo Senha Atual (só aparece se não for reset) -->
                <q-input
                  v-if="!isReset"
                  v-model="form.currentPassword"
                  :type="showCurrentPassword ? 'text' : 'password'"
                  label="Senha Atual"
                  outlined
                  :rules="[
                    val => !!val || 'Senha atual é obrigatória'
                  ]"
                  lazy-rules
                  :error="!!errors.currentPassword"
                  :error-message="errors.currentPassword"
                >
                  <template v-slot:prepend>
                    <q-icon name="lock" />
                  </template>
                  <template v-slot:append>
                    <q-icon
                      :name="showCurrentPassword ? 'visibility_off' : 'visibility'"
                      class="cursor-pointer"
                      @click="showCurrentPassword = !showCurrentPassword"
                    />
                  </template>
                </q-input>

                <!-- Campo Nova Senha -->
                <q-input
                  v-model="form.newPassword"
                  :type="showNewPassword ? 'text' : 'password'"
                  label="Nova Senha"
                  outlined
                  :rules="[
                    val => !!val || 'Nova senha é obrigatória',
                    val => val.length >= 6 || 'Senha deve ter no mínimo 6 caracteres',
                    val => !form.currentPassword || val !== form.currentPassword || 'Nova senha deve ser diferente da atual'
                  ]"
                  lazy-rules
                  :error="!!errors.newPassword"
                  :error-message="errors.newPassword"
                >
                  <template v-slot:prepend>
                    <q-icon name="lock_outline" />
                  </template>
                  <template v-slot:append>
                    <q-icon
                      :name="showNewPassword ? 'visibility_off' : 'visibility'"
                      class="cursor-pointer"
                      @click="showNewPassword = !showNewPassword"
                    />
                  </template>
                </q-input>

                <!-- Campo Confirmar Nova Senha -->
                <q-input
                  v-model="form.confirmPassword"
                  :type="showConfirmPassword ? 'text' : 'password'"
                  label="Confirmar Nova Senha"
                  outlined
                  :rules="[
                    val => !!val || 'Confirmação é obrigatória',
                    val => val === form.newPassword || 'As senhas não coincidem'
                  ]"
                  lazy-rules
                  :error="!!errors.confirmPassword"
                  :error-message="errors.confirmPassword"
                >
                  <template v-slot:prepend>
                    <q-icon name="verified_user" />
                  </template>
                  <template v-slot:append>
                    <q-icon
                      :name="showConfirmPassword ? 'visibility_off' : 'visibility'"
                      class="cursor-pointer"
                      @click="showConfirmPassword = !showConfirmPassword"
                    />
                  </template>
                </q-input>

                <!-- Requisitos de Senha -->
                <q-banner class="bg-blue-1 text-blue-9" rounded>
                  <template v-slot:avatar>
                    <q-icon name="info" color="blue" />
                  </template>
                  <div class="text-caption">
                    <strong>Requisitos da senha:</strong>
                    <ul class="q-pl-md q-my-sm">
                      <li>Mínimo de 6 caracteres</li>
                      <li>Combine letras e números para maior segurança</li>
                    </ul>
                  </div>
                </q-banner>

                <!-- Botão Submit -->
                <q-btn
                  type="submit"
                  color="primary"
                  :label="isReset ? 'Redefinir Senha' : 'Alterar Senha'"
                  :loading="loading"
                  unelevated
                  size="lg"
                  class="full-width q-mt-md"
                  icon-right="check_circle"
                />
              </q-form>
            </q-card-section>

           
          </q-card>

        
        </div>
      </q-page>
    </q-page-container>
  </q-layout>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import router from '@/router'
import store from '@/vuex/store'
import Swal from 'sweetalert2'

const route = useRoute()

const form = ref({
  currentPassword: '',
  newPassword: '',
  confirmPassword: ''
})

const errors = ref({
  currentPassword: '',
  newPassword: '',
  confirmPassword: ''
})

const showCurrentPassword = ref(false)
const showNewPassword = ref(false)
const showConfirmPassword = ref(false)
const loading = ref(false)
const resetToken = ref(null)

// Verifica se é uma operação de reset via query params
const isReset = computed(() => {
  return !!resetToken.value
})

onMounted(() => {
  // Captura o token de reset da URL se existir
  resetToken.value = route.query.token || route.query.reset || null
  
  if (resetToken.value) {
    console.log('Modo de reset ativado com token:', resetToken.value)
  }
})

const handleSubmit = async () => {
  loading.value = true
  errors.value = { currentPassword: '', newPassword: '', confirmPassword: '' }

  try {
    if (isReset.value) {
      // Operação de reset de senha (com token)
      await store.dispatch('auth/changePassword', {
        password: form.value.newPassword,
        password_confirmation: form.value.confirmPassword,
        forced: 1
      })

      Swal.fire({
        icon: 'success',
        title: 'Senha Redefinida',
        text: 'Sua senha foi redefinida com sucesso! Você pode fazer login agora.',
        confirmButtonText: 'OK'
      }).then(() => {
        router.push('/profile')
      })
    } else {
      // Operação normal de alteração de senha
      await store.dispatch('auth/changePassword', {
        password_actual: form.value.currentPassword,
        password: form.value.newPassword,
        password_confirmation: form.value.confirmPassword,
        forced: 0
      })

      Swal.fire({
        icon: 'success',
        title: 'Senha Alterada',
        text: 'Sua senha foi alterada com sucesso!',
        confirmButtonText: 'OK'
      }).then(() => {
        router.push('/profile')
      })
    }
  } catch (error) {
    console.error('Erro ao processar senha:', error)
    
    let errorMessage = 'Ocorreu um erro ao processar sua solicitação.'
    
    if (error.response?.data?.message) {
      errorMessage = error.response.data.message
    } else if (!isReset.value) {
      errorMessage = 'Senha atual incorreta. Por favor, tente novamente.'
    } else {
      errorMessage = 'Token inválido ou expirado. Solicite um novo link de recuperação.'
    }
    
    Swal.fire({
      icon: 'error',
      title: 'Erro',
      text: errorMessage
    })
  } finally {
    loading.value = false
  }
}

</script>

<style scoped>
.bg-gradient {
  background: linear-gradient(135deg, #e3f2fd 0%, #ffffff 50%, #e8eaf6 100%);
  min-height: 100vh;
}

.change-password-container {
  width: 100%;
  max-width: 450px;
  padding: 20px;
}

.change-password-card {
  border-radius: 16px;
  border: 1px solid #e0e0e0;
}

.shadow-10 {
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
}

@media (max-width: 600px) {
  .change-password-container {
    max-width: 100%;
  }
}
</style>